import { lruMemoize } from 'reselect';
import { style } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';
import { ToolbarColorTheme } from '../PublicTypes';

export const HiddenClassName = 'hidden';

const buttonCommonStyles: NestedCSSProperties = {
  alignItems: 'center',
  border: '1px solid transparent',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: '12px',
  fontWeight: 'bold',
  justifyContent: 'center',
  letterSpacing: '.2px',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

export const DefaultButtonStyleName = lruMemoize((colorTheme: ToolbarColorTheme, isSelected?: boolean, overrides?: NestedCSSProperties) =>
  style({
    ...buttonCommonStyles,
    backgroundColor: `${isSelected ? colorTheme.ButtonBackgroundActive : colorTheme.ToolbarBackground}`,
    color: `${colorTheme.ButtonContentNormal}`,
    height: '100%',
    minWidth: 40,
    paddingRight: 8,
    paddingLeft: 8,
    paddingTop: 0,
    paddingBottom: 0,
    $nest: {
      '&:hover:enabled': {
        backgroundColor: `${colorTheme.ButtonBackgroundHover}`,
      },
      '&:active:enabled': {
        backgroundColor: `${colorTheme.ButtonBackgroundPress}`,
      },
      '&:focus-visible': {
        boxSizing: 'border-box',
        boxShadow: 'none !important', // override VizClient styling
        outline: `2px ${colorTheme.ButtonFocusIndicator} solid !important`, // override VizClient styling
        outlineOffset: '-3px !important', // override VizClient styling
      },
      '&:disabled': {
        backgroundColor: `${colorTheme.ToolbarBackground}`,
        color: `${colorTheme.ButtonContentDisabled}`,
        cursor: 'default',
      },
      '.dropdown-arrow svg': {
        color: 'inherit',
        fill: 'currentColor',
      },
      '&:disabled .dropdown-arrow svg': {
        color: 'inherit',
        fill: 'currentColor',
      },
    },
    ...overrides,
  })
);

export const HighlightedButtonStyleName = lruMemoize((colorTheme: ToolbarColorTheme) =>
  style({
    ...buttonCommonStyles,
    backgroundColor: `${colorTheme.HighlightedButtonBackground}`,
    color: `${colorTheme.HighlightedButtonContent}`,
    borderRadius: 2,
    height: '60%',
    marginLeft: 11,
    marginRight: 11,
    paddingLeft: 8,
    paddingRight: 8,
    position: 'relative',
    top: '20%',
    $nest: {
      '&:hover:enabled': {
        backgroundColor: `${colorTheme.HighlightedButtonBackgroundHover}`,
        color: `${colorTheme.HighlightedButtonContent}`,
      },
      '&:active:enabled': {
        backgroundColor: `${colorTheme.HighlightedButtonBackgroundPress}`,
        color: `${colorTheme.HighlightedButtonContent}`,
      },
      '&:focus-visible': {
        backgroundColor: `${colorTheme.HighlightedButtonBackground}`,
        boxShadow: `inset 0 0 0 2px ${colorTheme.HighlightedButtonFocusInset} !important`, // override VizClient styling
        outline: `2px ${colorTheme.HighlightedButtonFocus} solid !important`, // override VizClient styling
        outlineOffset: -2,
        color: `${colorTheme.HighlightedButtonContent}`,
      },
      '&:disabled': {
        backgroundColor: `${colorTheme.HighlightedButtonBackgroundDisabled}`,
        color: `${colorTheme.HighlightedButtonContentDisabled}`,
        cursor: 'default',
      },
      '.dropdown-arrow svg': {
        color: 'inherit',
        fill: 'currentColor',
      },
      '&:disabled .dropdown-arrow svg': {
        color: 'inherit',
        fill: 'currentColor',
      },
    },
  })
);

const iconCommonStyles: NestedCSSProperties = {
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  display: 'inline-block',
};

export const IconStyleName = lruMemoize((iconSize: number) =>
  style({
    ...iconCommonStyles,
    backgroundSize: `${iconSize}px ${iconSize}px`,
    height: iconSize,
    width: iconSize,
  })
);

export const DropdownArrowContainerStyleName = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
});

export const LabelStyleName = style({
  marginLeft: '4px',
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const hiddenLabelStyles: NestedCSSProperties = {
  clip: 'rect(0,0,0,0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

export const HiddenLabelStyleName = style({
  ...hiddenLabelStyles,
});

export const SplitButtonOverrideStyles: NestedCSSProperties = {
  marginRight: 1,
  minWidth: 0,
  paddingLeft: 6,
  paddingRight: 6,
};

export const SplitButtonContainerStyleName = lruMemoize((colorTheme: ToolbarColorTheme) =>
  style({
    display: 'inline-flex',
    height: '100%',
    whiteSpace: 'nowrap',
    $nest: {
      '&:hover .button:enabled': {
        backgroundColor: `${colorTheme.ButtonBackgroundHover} !important`,
      },
      '&:hover .dropdown-button:enabled': {
        backgroundColor: `${colorTheme.SplitDropdownButtonBackgroundHover} !important`,
      },
      '&:active .button:enabled': {
        backgroundColor: `${colorTheme.ButtonBackgroundPress} !important`,
      },
      '&:active .dropdown-button:enabled': {
        backgroundColor: `${colorTheme.SplitDropdownButtonBackgroundPress} !important`,
      },
    },
  })
);

export const NoIconDropdownOverrideStyles: NestedCSSProperties = {
  minWidth: 0,
  paddingLeft: 2,
  paddingRight: 2,
};

export const PopoverButtonContainerStyleName = lruMemoize((colorTheme: ToolbarColorTheme) =>
  style({
    height: '100%',
    $nest: {
      '.popover-button[aria-controls]': {
        backgroundColor: `${colorTheme.ButtonBackgroundActive} !important`,
      },
    },
  })
);
